import { Duration, Input, ListBox, TextArea } from '../../../../components';
import { FormFields, FormHeader, FormSection } from '../form';
import { useValidation } from '../../../../hooks';
import { useFormContext } from 'react-hook-form';
import { claimFields } from '../../../../config';

import type { IClaim, IClaimInit } from '../../../../types';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useContext } from 'react';
import { Localization } from '../../../../config/localization/localization';
import { LanguageSelectorContext } from '../../../../context/LanguageSelectorContext';

interface IDamageDescriptionProps {
  claimInit?: IClaimInit | IClaim | null;
  setSaveAndQuitChanges?: Dispatch<SetStateAction<Array<string>>>;
}

export const DamageDescription: FC<IDamageDescriptionProps> = ({ claimInit, setSaveAndQuitChanges }) => {
  const formContext = useFormContext();
  const { selectedLanguage } = useContext(LanguageSelectorContext);
  const localization = new Localization(selectedLanguage);

  const { fieldRequirements } = useValidation({});

  const onDetailsOfDamageLossChange = (newValue: string) => {
    setSaveAndQuitChanges?.((prev) => [...prev, 'DetailsofDamageLoss']);
    formContext.setValue(claimFields.detailsOfDamageLoss, newValue);
    formContext.clearErrors(claimFields.detailsOfDamageLoss);
  };

  return (
    <FormSection>
      <FormHeader>{localization.translate('damageDescription.header')}</FormHeader>

      <FormFields>
        {/* DESCRIPTION */}
        <TextArea
          options={{
            isRequired: fieldRequirements.DetailsOfDamageLoss,
            label: localization.translate('damageDescription.descriptionLabel'),
            name: claimFields.detailsOfDamageLoss,
            onChange: onDetailsOfDamageLossChange,
          }}
          tooltip={localization.translate('damageDescription.descriptionTooltip')}
        />

        {/* ESTIMATED SUBS AFFECTED */}
        <Input
          inputOptions={{
            isRequired: fieldRequirements.EstimatedSubsAffected,
            label: localization.translate('damageDescription.subsAffectedLabel'),
            name: claimFields.estimatedSubsAffected,
            onChange: (_) => setSaveAndQuitChanges?.((prev) => [...prev, 'SubAffected']),
          }}
          tooltip={localization.translate('damageDescription.subsAffectedTooltip')}
          type={'number'}
        />

        {/* OUTAGE DURATION */}
        <Duration
          isRequired={fieldRequirements.OutageDuration}
          label={localization.translate('damageDescription.outageDurationLabel')}
          name={claimFields.outageDuration}
          onChange={(_) => setSaveAndQuitChanges?.((prev) => [...prev, 'OutageDuration'])}
          tooltip={localization.translate('damageDescription.outageDurationTooltip')}
        />

        {/* ACTIVITY TYPE */}
        <ListBox
          isRequired={fieldRequirements.DamageActivityType}
          label={localization.translate('damageDescription.activityTypeLabel')}
          listItems={claimInit?.damageActivityTypeList ?? []}
          name={claimFields.damageActivityType}
          onSingleChange={(_) => setSaveAndQuitChanges?.((prev) => [...prev, 'DamageActivityTypeId'])}
          tooltip={localization.translate('damageDescription.activityTypeTooltip')}
        />

        {/* DAMAGE BY */}
        <ListBox
          isRequired={fieldRequirements.DamageByType}
          label={localization.translate('damageDescription.damageByLabel')}
          listItems={claimInit?.damageByTypeList ?? []}
          name={claimFields.damageByType}
          onSingleChange={(_) => setSaveAndQuitChanges?.((prev) => [...prev, 'DamageByTypeId'])}
          tooltip={localization.translate('damageDescription.damageByTooltip')}
        />
      </FormFields>
    </FormSection>
  );
};